// AmbientDetail primitive (Set10) — the fragment-shader ambient-detail normal (RESIDUAL design,
// 2026-08-07, after the user rejected both the zoom-handoff crossover AND the "continuation"
// concept — the fractal is exactly what the user configured; finer granularity = raise octaves).
//
// Plan: Set10_BandLimitedDetailNormal.md.  The terrain's own ambient noise is baked into the
// per-chunk height lattice at CHUNK_N resolution, and a central difference at that spacing
// cannot represent octaves with wavelength < 2*cellSize (§1.1 — the Nyquist root cause: the
// only octaves that produce visible shading are the large ones, whose slope is tiny relative
// to the hill).  The lattice NORMAL (matGeoNormal, from the worker's heights) carries D at
// lattice spacing — call that ∇D_cellSize.  This primitive adds the RESIDUAL
// `∇D_px − ∇D_cellSize` for every D octave (λ <= maxWavelengthAsDetail):
//
//   total D shading = ∇D_cellSize (lattice normal, already applied) + (∇D_px − ∇D_cellSize)
//                   = ∇D_px
//
// So D shades EXACTLY ONCE, at PIXEL scale (the hillside-visible detail, req 1), never doubled
// (req 3), with no octave moving between CPU and GPU based on zoom (req 5 — ALL D octaves are
// always in both the lattice and the shader; the residual smoothly varies with zoom: at deep
// zoom ∇D_cellSize ≈ ∇D_px so the residual ≈ 0 and the lattice carries it; at coarse zoom
// ∇D_cellSize ≈ 0 so the residual ≈ ∇D_px — a continuous correction, not a membership flip).
// `detailNormalStrength` (u_ambDetailStrength) multiplies the WHOLE residual AND gates the CPU
// evalD (the lattice's D is scaled by the same factor), so 0 removes D everywhere (req 4).
//
// The GLSL sampler mirrors the CPU path's fractal (IsolineEvaluator.buildAmbientLDContext):
// per-octave FastNoiseLite instances seeded `worldSeed + octaveIndex * OCTAVE_PRIME`, frequency
// 1/wavelength (freq * lacunarity per octave), amp * persistence per octave.  The noise itself
// is a Value-noise approximation (FastNoiseLite's _SingleValueR2 hash chain, see
// ambientValCoord) — for `algorithm: "value"` this is a faithful port; for opensimplex2/perlin
// it is the Set10 §5.2 decision (a): ACCEPT the mismatch.
//
// Octave membership (world-fixed, zoom-independent — NO continuation):
//   o < u_ambDetailOctaves            -> D octave: residual ∇px − ∇cellSize (in BOTH renderers)
//   u_ambDetailOctaves <= o < octaves -> L octave: lattice only (the shader skips it)
//   o >= octaves                      -> NOT EVALUATED (the fractal is exactly the configured
//                                         octave count; finer detail = the user raises octaves).
// The loop is a CONSTANT-bound `for (o = 0; o < AMBIENT_DETAIL_OCTAVE_CAP; o++)` (D7 — a dynamic
// bound is not portable-unrollable in GLSL ES 300) with a uniform break at the configured
// octave count (the Fbm.glsl precedent).  Every smoothstep has ASCENDING edges (§5.1 — the
// inverted form is driver-undefined).  Sub-pixel octaves (λ < ~2px) fade to mean per-octave
// (the AA layer — world-fixed wavelengths + pixel AA, Set8 Q5; NOT a membership handoff).
//
// NOTE (repo hazard): no backticks and no GLSL-ES-300 reserved identifiers (flat/smooth/...)
// inside the template literal.
export const AMBIENT_DETAIL_GLSL = `
// Shader octave loop cap — a CONSTANT loop bound (D7: a dynamic bound is not
// portable-unrollable in GLSL ES 300).  MUST be >= the maximum configured octave count
// (the user's realistic config is 18; the JS side clamps u_ambOctaves to this same value).
// Raised from the old 8 because the user correctly wants detail even when zoomed in close.
const int AMBIENT_OCTAVE_LOOP_CAP = 18;

// FastNoiseLite _ValCoordR2 (imul-chain): hash = seed ^ imul(x,PX) ^ imul(y,PY) — the XOR of the
// two primed coords, NOT their sum — then imul 0x27d4eb2d, square, xor-shift 19, * (1/2^31).
// uint math for defined wrapping; the final signed mapping reproduces the JS int32 semantics.
// (float(h) loses low mantissa bits for large hashes — same accepted precision loss as
// Hash.glsl's hash01, and the Set10 §5.2 decision (a) accepts the approximation anyway.)
float ambientValCoord(uint seed, int cellX, int cellY) {
  uint h = seed ^ (uint(cellX) * 501125321u) ^ (uint(cellY) * 1136930381u);
  h = h * 0x27d4eb2du;
  h = h * h;
  h = h ^ (h << 19u);
  float f = float(h);
  if (f >= 2147483648.0) f -= 4294967296.0;
  return f * (1.0 / 2147483648.0);
}

// 2D value noise over p (cell units), FastNoiseLite Value-compatible given the per-octave seed.
// Port of _SingleValueR2: floor lattice, Hermite interp, _ValCoordR2 corners.
// (Used only by ambientDetailNormal; valueNoise() in Fbm.glsl is a DIFFERENT hash — do not merge.)
float ambientValueNoise(vec2 p, uint seed) {
  vec2 f = floor(p);
  vec2 xf = p - f;
  vec2 s = xf * xf * (3.0 - 2.0 * xf);
  ivec2 i = ivec2(f);
  float v00 = ambientValCoord(seed, i.x, i.y);
  float v10 = ambientValCoord(seed, i.x + 1, i.y);
  float v01 = ambientValCoord(seed, i.x, i.y + 1);
  float v11 = ambientValCoord(seed, i.x + 1, i.y + 1);
  return mix(mix(v00, v10, s.x), mix(v01, v11, s.x), s.y);
}

// Per-octave sub-pixel AA fade: 1 for octaves with cell >= ~2px (fully present), 0 below ~1px
// (fade toward the mean instead of sizzling — Fbm.glsl's band-limit convention).  Spec-clean
// ascending edges.  This is the ONLY zoom-dependent factor — it is the AA layer, NOT a
// CPU/GPU membership handoff (world-fixed wavelengths, pixel AA — Set8 Q5).
float ambientPixelFade(float cellMeters) {
  float px = matPixelSize();
  return smoothstep(max(1.0 * px, 1e-5), max(2.0 * px, 2e-5), cellMeters);
}

// The residual ambient detail normal at p (world meters): for every D octave
// (o < u_ambDetailOctaves), the height-gradient at PIXEL spacing minus the height-gradient at
// LATTICE spacing (the lattice normal's D, which the worker's central difference already
// applied) — net ∇D_px.  Octaves between u_ambDetailOctaves and u_ambOctaves are L (lattice
// only — the shader skips them); the fractal stops at u_ambOctaves (no continuation).  Scaled
// to the normals' slope convention (nx = -dh/dx * slopeScale, nz = 1 — mirroring
// TerrainWorker.computeNormalsFromHeights; the whiteout blend in emitEval combines this with
// the recipe's own detail normal).  Returns the raw slope vector (NOT unit-normalised) — the
// detail-normal-map convention where xy carries the slope offsets.
vec3 ambientDetailNormal(vec2 p) {
  float px = matPixelSize();
  float wavelength = u_ambWavelength;
  float lacunarity = u_ambLacunarity;
  float persistence = u_ambPersistence;
  float amplitude = u_ambAmplitude;
  int octaves = u_ambOctaves;
  int detailOctaves = u_ambDetailOctaves;
  int mode = u_ambFractalType;
  float seedBase = u_ambSeed;
  vec2 offset = u_ambOffset;

  float lc = max(lacunarity, 1.0001);
  float amp = 1.0;          // octave 0 amplitude (persistence^o per octave)
  float dhdx = 0.0;
  float dhdz = 0.0;
  float normSum = 0.0;      // fade-weighted amp sum (the "effective" fractal weight)
  float cellMeters = max(wavelength, 1e-5);
  // CONSTANT-bound loop (D7): iterate up to the cap; a uniform break stops at the configured
  // octave count (the Fbm.glsl precedent).  D octaves (o < detailOctaves) accumulate the pixel
  // gradient; L octaves (detailOctaves <= o < octaves) advance only.  amp/cellMeters advance
  // every iteration regardless of skip (per-octave properties).
  for (int o = 0; o < AMBIENT_OCTAVE_LOOP_CAP; o++) {
    if (o >= octaves) break;
    if (o < detailOctaves) {
      float fade = ambientPixelFade(cellMeters);
      if (fade > 0.0) {
        // Per-octave noise instance (CPU path: seed = worldSeed + o * OCTAVE_PRIME; OCTAVE_PRIME
        // = 198491317 — uint add for defined wrapping, matching the JS ToInt32 coercion).
        uint octSeed = uint(int(seedBase)) + uint(o) * 198491317u;
        float wl = max(cellMeters, 1e-5);
        float fx = 1.0 / wl;
        vec2 sp = p * fx + offset;   // frequency scaling + offset (mirrors the CPU's x*freq + ox)
        // Gradient tap spacing = λ/8 — a fixed small fraction of the octave's wavelength (90%
        // of the true slope for a sine).  NOT max(px, λ/8): at wide zoom px grows past λ/8 and
        // the two taps then span >½λ, aliasing the gradient into garbage (the "blurry at wide
        // zoom" + amplified chunk seams).  Sub-pixel octaves are already faded out by
        // ambientPixelFade, so the tap needs no px floor.
        float tap = max(wl * 0.125, 1e-5);
        float vxp = ambientValueNoise(sp + vec2(tap * fx, 0.0), octSeed);
        float vxm = ambientValueNoise(sp - vec2(tap * fx, 0.0), octSeed);
        float vzp = ambientValueNoise(sp + vec2(0.0, tap * fx), octSeed);
        float vzm = ambientValueNoise(sp - vec2(0.0, tap * fx), octSeed);
        // Mode transforms (Fbm.glsl conventions): 1 = ridged (1-|v|), 2 = billow (-|v|),
        // 3 = turbulence (|v|).  Applied to the taps BEFORE the difference (the transform of the
        // difference is not the difference of the transform).
        if (mode == 1) { vxp = 1.0 - abs(vxp); vxm = 1.0 - abs(vxm); vzp = 1.0 - abs(vzp); vzm = 1.0 - abs(vzm); }
        else if (mode == 2) { vxp = -abs(vxp); vxm = -abs(vxm); vzp = -abs(vzp); vzm = -abs(vzm); }
        else if (mode == 3) { vxp = abs(vxp); vxm = abs(vxm); vzp = abs(vzp); vzm = abs(vzm); }
        // Raw slope gradient (the true per-metre slope of the D field; ∝ 1/λ).  The
        // fade-weighted normalization below (normSum) keeps the GAIN consistent across zooms
        // even though the raw slope is zoom-dependent — the physical slope is preserved, which
        // is what a high-persistence fractal genuinely is (rougher at fine scale).
        float dvdx = (vxp - vxm) / (2.0 * tap);
        float dvdz = (vzp - vzm) / (2.0 * tap);
        float w = amp * fade;
        dhdx += w * dvdx;
        dhdz += w * dvdz;
        normSum += w;
      }
    }
    amp *= persistence;
    cellMeters /= lc;
  }

  // Zoom-invariant normalization: divide by the FADE-WEIGHTED amp sum (the octaves actually
  // contributing at this zoom), NOT totalMaxAmp (which includes faded-out octaves and made the
  // gain collapse at wide zoom).  The result is the physical slope of the D field, weighted by
  // the octaves that are actually resolvable at this zoom — so detailStrength is a gain knob
  // with the SAME meaning at every zoom (0 = off, 1 = natural, >1 = exaggerated), independent
  // of persistence (the old totalMaxAmp divisor collapsed the gain at wide zoom for ANY config).
  if (normSum > 1e-5) {
    dhdx /= normSum;
    dhdz /= normSum;
  }
  // Relief = frac * amplitude * detailStrength (the CPU's evalD convention: frac is normalised,
  // the *0.5 is the codebase's noise-to-relief half-range, the amplitude is the master gain for
  // the D HEIGHT).  The normal follows the worker's slope convention: nx = -dh/dx * slopeScale
  // (matAmbientSlopeScale — the Set10 §5.3 decision: the shader band is exaggerated consistently
  // with the geometric band; the unlit path's indirection returns 1.0 since u_slopeScale only
  // exists with lighting), nz = 1.
  float reliefScale = amplitude * 0.5 * u_ambDetailStrength;
  float slopeScale = matAmbientSlopeScale();
  // Issue 2 (2026-08-08) — the per-chunk ambient-weight mask: the CPU multiplies the whole
  // ambient term by ambientWeightMultiplier(x,y) (innermost closed-ring wins); the lattice
  // normal already carries L·mult, so the shader's D must be scaled by the SAME mask value
  // or a 0-multiplier coastline ring leaves bumpy D on lakebeds.  The mask is the worker's
  // CHUNK_N×CHUNK_N lattice (R32F) sampled with the same round() mapping as matGeoNormal —
  // a LITERAL 32.0 (CHUNK_N-1) because the #define CHUNK_N only exists in the splat/lighting
  // paths (the unlit non-splat assemble must still compile; the u_ambCellSize precedent).
  float weight = 1.0;
  if (u_ambientWeightMaskEnabled == 1) {
    ivec2 lat = ivec2(round((p - u_origin) / max(u_chunkSize, 1e-6) * 32.0));
    weight = texelFetch(u_ambientWeightMask, lat, 0).r;
  }
  return vec3(-dhdx * reliefScale * slopeScale * weight, -dhdz * reliefScale * slopeScale * weight, 1.0);
}
`;
